export const SUITS = ['C', 'D', 'H', 'S'] as const;
export type Suit = (typeof SUITS)[number];

export const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'] as const;
export type StandardRank = (typeof RANKS)[number];
export type Rank = StandardRank | 'JK';

export interface Card {
  /** Unique within a match, stable across moves: `<rank><suit>#<deck>` or `JK<n>#<deck>`. */
  readonly id: string;
  readonly rank: Rank;
  /** Jokers have no suit. */
  readonly suit: Suit | null;
  /** Which physical deck in a multi-deck shoe this card came from. */
  readonly deck: number;
}

export interface DeckOptions {
  readonly jokers?: number;
  readonly deckIndex?: number;
}

/** A standard 52-card deck, ordered by suit then rank, plus optional jokers. */
export function standardDeck({ jokers = 0, deckIndex = 0 }: DeckOptions = {}): Card[] {
  if (!Number.isInteger(jokers) || jokers < 0) throw new RangeError(`jokers must be a non-negative integer`);
  const cards: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      cards.push({ id: `${rank}${suit}#${deckIndex}`, rank, suit, deck: deckIndex });
    }
  }
  for (let j = 0; j < jokers; j++) {
    cards.push({ id: `JK${j}#${deckIndex}`, rank: 'JK', suit: null, deck: deckIndex });
  }
  return cards;
}

/** Concatenate `decks` standard decks with unique ids (FR-CARD-08). */
export function shoe(decks: number, options: Omit<DeckOptions, 'deckIndex'> = {}): Card[] {
  if (!Number.isInteger(decks) || decks < 1) throw new RangeError(`decks must be a positive integer`);
  return Array.from({ length: decks }, (_, i) => standardDeck({ ...options, deckIndex: i })).flat();
}

/** Rank ordering, e.g. ace-high for War, ace-low for Old Maid scoring. */
export interface RankOrder {
  value(rank: Rank): number;
  compare(a: Card, b: Card): number;
}

export function rankOrder(order: readonly Rank[]): RankOrder {
  if (new Set(order).size !== order.length) throw new Error('rankOrder contains duplicates');
  const values = new Map<Rank, number>(order.map((r, i) => [r, i]));
  const value = (rank: Rank): number => {
    const v = values.get(rank);
    if (v === undefined) throw new Error(`Rank ${rank} is not in this ordering`);
    return v;
  };
  return { value, compare: (a, b) => value(a.rank) - value(b.rank) };
}

export const ACE_HIGH = rankOrder(RANKS);
export const ACE_LOW = rankOrder(['A', ...RANKS.slice(0, -1)]);
